import type { Action, ActionId, ExtractInput, InputValues } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { Button, Drawer, Field, Input, Select, Textarea } from "../../ui/index.js";
import { actionOptionLabel, approvesByDefault, isRunnable } from "./actionChoice.js";

interface OverrideDrawerProps {
  open: boolean;
  onClose: () => void;
  messageId: string;
  /** The action that already finished on this message, if any. */
  alreadyRan: string | null;
  onDone: () => void;
}

function extractInputs(action: Action | undefined): ExtractInput[] {
  if (action === undefined || action.type === "hold" || action.type === "ignore") return [];
  return action.inputs.flatMap((input) => (input.source === "extract" ? [input] : []));
}

function toValue(input: ExtractInput, raw: string): string | number | boolean {
  if (input.type === "number") return Number(raw);
  if (input.type === "boolean") return raw === "true";
  return raw;
}

function collectValues(inputs: readonly ExtractInput[], typed: Record<string, string>): { values: InputValues; missing: string[] } {
  const values: InputValues = {};
  const missing: string[] = [];
  for (const input of inputs) {
    const raw = (typed[input.name] ?? "").trim();
    if (raw === "") {
      if (input.required) missing.push(input.name);
      continue;
    }
    values[input.name] = toValue(input, raw);
  }
  return { values, missing };
}

/** "Change action": run a different action on a message, with the values the router would have found. */
export function OverrideDrawer({ open, onClose, messageId, alreadyRan, onDone }: OverrideDrawerProps) {
  const api = useApi();
  const { actions, reloadApprovals } = useTenant();
  const task = useTask();
  const choices = actions.filter((action) => action.enabled);
  const [actionId, setActionId] = useState<string>("");
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [addExample, setAddExample] = useState(true);
  const chosen = choices.find((action) => action.id === actionId);
  const inputs = extractInputs(chosen);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (chosen === undefined) return;
    const { values, missing: gaps } = collectValues(inputs, typed);
    setMissing(gaps);
    if (gaps.length > 0) return;
    const target: ActionId = chosen.id;
    const ok = await task.run(async () => {
      await api.call("messageOverride", { params: { id: messageId }, body: { actionId: target, inputs: values, needsApproval, addExample } });
      await reloadApprovals();
    }, needsApproval ? `Changed to "${chosen.name}". It waits for your approval.` : `Changed to "${chosen.name}".`);
    if (ok) {
      setActionId("");
      setTyped({});
      setNeedsApproval(false);
      setAddExample(true);
      onDone();
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Change action"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="override-form" busy={task.busy} disabled={chosen === undefined}>
            {chosen === undefined ? "Run action" : needsApproval ? `Run "${chosen.name}" after approval` : `Run "${chosen.name}"`}
          </Button>
        </>
      }
    >
      <form id="override-form" className="stack" onSubmit={(event) => void submit(event)}>
        {alreadyRan === null ? null : (
          <div className="note note-warn" role="note">
            "{alreadyRan}" already ran on this message. The new action runs as well; it does not undo that one.
          </div>
        )}
        <Field label="Action">
          {({ id }) => (
            <Select
              id={id}
              value={actionId}
              onChange={(event) => {
                setActionId(event.target.value);
                setMissing([]);
                setNeedsApproval(approvesByDefault(choices.find((action) => action.id === event.target.value)));
              }}
            >
              <option value="" disabled>
                Pick an action
              </option>
              {choices.map((action) => (
                <option key={action.id} value={action.id}>
                  {actionOptionLabel(action)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {chosen === undefined ? null : <p className="muted small">{chosen.description}</p>}
        {inputs.map((input) => (
          <OverrideInputField
            key={input.name}
            input={input}
            value={typed[input.name] ?? ""}
            error={missing.includes(input.name) ? "Fill this in." : undefined}
            onChange={(value) => { setTyped({ ...typed, [input.name]: value }); }}
          />
        ))}
        {isRunnable(chosen) ? (
          <label className="checkbox">
            <input type="checkbox" checked={needsApproval} onChange={(event) => { setNeedsApproval(event.target.checked); }} />
            <span>Wait for my approval before it runs</span>
          </label>
        ) : null}
        {chosen === undefined ? null : (
          <label className="checkbox">
            <input type="checkbox" checked={addExample} onChange={(event) => { setAddExample(event.target.checked); }} />
            <span>Teach the router to pick "{chosen.name}" for messages like this one</span>
          </label>
        )}
      </form>
    </Drawer>
  );
}

interface OverrideInputFieldProps {
  input: ExtractInput;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
}

function OverrideInputField({ input, value, error, onChange }: OverrideInputFieldProps) {
  return (
    <Field label={`${input.name}${input.required ? "" : " (optional)"}`} hint={input.description} error={error}>
      {({ id, describedBy, invalid }) => {
        const common = { id, "aria-describedby": describedBy, "aria-invalid": invalid };
        if (input.type === "boolean") {
          return (
            <Select {...common} value={value} onChange={(event) => { onChange(event.target.value); }}>
              <option value="">Choose</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          );
        }
        if (input.type === "text") return <Textarea {...common} rows={3} value={value} onChange={(event) => { onChange(event.target.value); }} />;
        return (
          <Input
            {...common}
            type={input.type === "number" ? "number" : input.type === "email" ? "email" : "text"}
            value={value}
            onChange={(event) => { onChange(event.target.value); }}
          />
        );
      }}
    </Field>
  );
}
